import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useFilteredBatchStore } from './useFilteredBatchStore'
import { SyncTargetSchema, useAllSyncTargetSchemasQuery } from '@/api/schemas'
import { extractFilterableFields, FilterableField } from '@/utils/field-schema-helper'

export interface BatchListSort {
  // Static well-known fields are kept as literals; dynamic custom-data keys are plain strings.
  field: string
  direction: 'asc' | 'desc'
}

type InvoiceSortOption = {
  label: string
  field: string
  /**
   * When set, this sort option is only included when an installed sync-target
   * app matches this type string (e.g. 'qbo').  Omit for options that are
   * always available.
   */
  appType?: string
}

const STATIC_SORT_OPTIONS: ReadonlyArray<InvoiceSortOption> = [
  { label: 'Batch number', field: 'BatchNumber' },
  { label: 'Batch date', field: 'BatchDate' },
  { label: 'Due date', field: 'DueDate' },
  { label: 'Created', field: 'Created' },
  { label: 'Modified', field: 'Modified' },
  { label: 'Qbo Synced At', field: 'QboLastSynced', appType: 'qbo' },
  { label: 'Qbo Sync Status', field: 'QboSyncStatus', appType: 'qbo' },
]

/**
 * Maps a field definition type to the backend FilterKind string.
 * Returns null for types that cannot be sorted.
 */
function toFilterKind(field: FilterableField): string | null {
  const def = field.definition
  // Lookup fields use their entity-specific filter kind
  if (def.lookup?.entity) {
    const LOOKUP_ENTITY_TO_FILTER_KIND: Record<string, string> = {
      user: 'StringLookup',
      location: 'IntLookup',
      business_unit: 'StringLookup',
      company: 'IntLookup',
    }
    return LOOKUP_ENTITY_TO_FILTER_KIND[def.lookup.entity] ?? null
  }
  if (def.type === 'boolean') return 'Boolean'
  if (def.type === 'integer') return 'Integer'
  if (def.type === 'number') return 'Decimal'
  if (def.type === 'string') {
    if (def.format === 'date' || def.format === 'date-time') return 'DateTime'
    return 'Text'
  }
  return null
}

/**
 * Builds sort options for all custom-data fields found across the provided
 * sync-target schemas using extractFilterableFields to handle nested fields.
 * The field key follows the backend format:
 *   CustomData|{appType}|{FilterKind}|{pathKey}|{fieldTitle}
 */
function buildCustomDataSortOptions(schemas: SyncTargetSchema[]): InvoiceSortOption[] {
  // Detect duplicate labels across sync targets so we can prefix with the target name.
  const labelCounts = new Map<string, number>()
  for (const schema of schemas) {
    const schemaObj = schema.schema?.schema
    if (!schemaObj) continue
    const fields = extractFilterableFields(
      schemaObj,
      schema.schema.uiSchema ?? {},
      schemaObj.definitions ?? {},
      true,
    )
    for (const field of fields) {
      labelCounts.set(field.label, (labelCounts.get(field.label) ?? 0) + 1)
    }
  }

  const options: InvoiceSortOption[] = []

  for (const schema of schemas) {
    const schemaObj = schema.schema?.schema
    if (!schemaObj) continue

    const sortableKeys = new Set(schema.schema?.uiSchema?.['ui:sortable'] ?? [])

    const fields = extractFilterableFields(
      schemaObj,
      schema.schema.uiSchema ?? {},
      schemaObj.definitions ?? {},
      true,
    )

    for (const field of fields) {
      // Only root-level, non-array fields can be sorted
      if (field.isNested || field.hasArrayAncestor) continue
      if (!sortableKeys.has(field.pathKey)) continue

      const filterKind = toFilterKind(field)
      if (!filterKind) continue

      const needsPrefix = (labelCounts.get(field.label) ?? 0) > 1
      const label = needsPrefix ? `${schema.name} ${field.label}` : field.label

      options.push({
        label,
        field: `CustomData|${schema.type}|${filterKind}|${field.pathKey}|${field.definition.title}`,
      })
    }
  }

  return options
}

export const useBatchSort = () => {
  const invoiceStore = useFilteredBatchStore()
  const { sort: currentSort } = storeToRefs(invoiceStore)
  const { updateSorting } = invoiceStore

  const { data: syncTargetSchemas } = useAllSyncTargetSchemasQuery()

  const allSortOptions = computed<InvoiceSortOption[]>(() => {
    const installedAppTypes = new Set((syncTargetSchemas.value ?? []).map((s) => s.type))
    const visibleStatic = STATIC_SORT_OPTIONS.filter(
      (o) => !o.appType || installedAppTypes.has(o.appType),
    )
    return [...visibleStatic, ...buildCustomDataSortOptions(syncTargetSchemas.value ?? [])]
  })

  const currentSortLabel = computed(() => {
    const match = allSortOptions.value.find((o) => o.field === currentSort.value.field)
    return match ? match.label : STATIC_SORT_OPTIONS[0].label
  })

  const currentSortIcon = computed(() =>
    currentSort.value.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down',
  )

  const isActiveSort = (field: string) => currentSort.value.field === field

  const getDirectionIcon = (field: string) => {
    if (!isActiveSort(field)) return 'mdi-swap-vertical'
    return currentSort.value.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'
  }

  const toggleSort = (field: string) => {
    const next: BatchListSort = {
      field,
      direction: 'desc',
    }
    if (isActiveSort(field)) {
      next.direction = currentSort.value.direction === 'asc' ? 'desc' : 'asc'
    }
    updateSorting(next)
  }

  const menuItems = computed(() =>
    allSortOptions.value.map((option) => ({
      text: option.label,
      field: option.field,
      icon: getDirectionIcon(option.field),
    })),
  )

  return {
    currentSort,
    currentSortLabel,
    currentSortIcon,
    isActiveSort,
    toggleSort,
    menuItems,
  }
}
